import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  Upload,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  FolderOutlined,
  RightOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SwapOutlined,
  TeamOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import Layout from '../components/Layout';
import AdminTrainingDocuments from '../components/AdminTrainingDocuments';
import ReplaceTrainingFile from '../components/ReplaceTrainingFile';
import AdminVideoSubtitlesModal from '../components/AdminVideoSubtitlesModal';
import AdminTrainingPreview, { AdminMaterialSelection } from '../components/AdminTrainingPreview';
import api from '../services/api';
import { TrainingFolder, TrainingVideo, VideoStatisticsRow } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import { dateMismatchMessage, formatTrainingDate, formatUploadDate, inferTrainingDate } from '../utils/trainingDates';
import { trainingUploadError, validateTrainingFile } from '../utils/trainingUpload';
import './VideoPortal.css';
import './AdminVideoPortal.css';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type ViewMode = 'content' | 'statistics';
type FolderFilter = 'all' | 'active' | 'archived';
type StatisticsFilter = 'all' | 'completed' | 'progress';

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
};

const formatDuration = (seconds?: number | null) => {
  if (!seconds) return 'Время не указано';
  const minutes = Math.floor(seconds / 60);
  const restSeconds = Math.round(seconds % 60);
  return `${minutes}:${String(restSeconds).padStart(2, '0')}`;
};

const AdminVideoPortal: React.FC = () => {
  const [folders, setFolders] = useState<TrainingFolder[]>([]);
  const [statistics, setStatistics] = useState<VideoStatisticsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statisticsError, setStatisticsError] = useState('');
  const loadRequestId = useRef(0);
  const [viewMode, setViewMode] = useState<ViewMode>('content');
  const [folderFilter, setFolderFilter] = useState<FolderFilter>('all');
  const [statisticsFilter, setStatisticsFilter] = useState<StatisticsFilter>('all');
  const [folderSearch, setFolderSearch] = useState('');
  const [statisticsSearch, setStatisticsSearch] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(() => new Set());
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [editingFolder, setEditingFolder] = useState<TrainingFolder | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [editingVideo, setEditingVideo] = useState<TrainingVideo | null>(null);
  const [replacingVideo, setReplacingVideo] = useState<TrainingVideo | null>(null);
  const [subtitleVideo, setSubtitleVideo] = useState<TrainingVideo | null>(null);
  const [preview, setPreview] = useState<AdminMaterialSelection | null>(null);
  const [savingVideo, setSavingVideo] = useState(false);
  const [publishingFolderId, setPublishingFolderId] = useState<number | null>(null);
  const [updatingVideoId, setUpdatingVideoId] = useState<number | null>(null);
  const [deletingVideoId, setDeletingVideoId] = useState<number | null>(null);
  const [deletingFolderId, setDeletingFolderId] = useState<number | null>(null);
  const [folderForm] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [videoForm] = Form.useForm();
  const uploadTrainingDate = Form.useWatch('training_date', uploadForm);
  const editedTrainingDate = Form.useWatch('training_date', videoForm);
  const uploadDateWarning = uploadFile ? dateMismatchMessage(uploadTrainingDate?.format('YYYY-MM-DD'), uploadFile.name) : null;
  const editedDateWarning = editingVideo ? dateMismatchMessage(editedTrainingDate?.format('YYYY-MM-DD'), editingVideo.original_filename) : null;

  const loadData = useCallback(async () => {
    const requestId = ++loadRequestId.current;
    setLoading(true);
    setError('');
    setStatisticsError('');
    await Promise.all([
      api.get<TrainingFolder[]>('/video/admin/folders')
        .then(({ data }) => {
          if (requestId !== loadRequestId.current) return;
          setFolders(data);
          setExpandedFolderIds(current => new Set(Array.from(current).filter(id => data.some(folder => folder.id === id))));
          setSelectedFolderId((currentId) => (
            data.some(folder => folder.id === currentId) ? currentId : data[0]?.id ?? null
          ));
        })
        .catch((err) => {
          if (requestId === loadRequestId.current) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить папки и видео'));
          }
        })
        .finally(() => {
          if (requestId === loadRequestId.current) setLoading(false);
        }),
      api.get<VideoStatisticsRow[]>('/video/admin/statistics')
        .then(({ data }) => {
          if (requestId === loadRequestId.current) setStatistics(data);
        })
        .catch((err) => {
          if (requestId === loadRequestId.current) {
            setStatisticsError(getApiErrorMessage(err, 'Не удалось загрузить статистику'));
          }
        }),
    ]);
  }, []);

  useEffect(() => {
    loadData();
    return () => { loadRequestId.current += 1; };
  }, [loadData]);

  const setFolderExpanded = (folderId: number, expanded: boolean) => {
    setExpandedFolderIds(current => {
      const next = new Set(current);
      if (expanded) next.add(folderId);
      else next.delete(folderId);
      return next;
    });
    if (expanded) setSelectedFolderId(folderId);
  };

  const openFolderModal = (folder?: TrainingFolder) => {
    setEditingFolder(folder || null);
    folderForm.resetFields();
    folderForm.setFieldsValue(folder || { sort_order: 0, is_active: true });
    setFolderModalOpen(true);
  };

  const saveFolder = async () => {
    if (savingFolder) return;
    setSavingFolder(true);
    try {
      const values = await folderForm.validateFields();
      if (editingFolder) await api.patch(`/video/admin/folders/${editingFolder.id}`, values);
      else await api.post('/video/admin/folders', values);
      message.success(editingFolder ? 'Папка обновлена' : 'Папка создана');
      setFolderModalOpen(false);
      loadData();
    } catch (err: any) {
      if (!err?.errorFields) message.error(getApiErrorMessage(err, 'Не удалось сохранить папку'));
    } finally {
      setSavingFolder(false);
    }
  };

  const archiveFolder = async (folderId: number) => {
    try {
      await api.delete(`/video/admin/folders/${folderId}`);
      message.success('Папка перенесена в архив');
      await loadData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Не удалось архивировать папку');
    }
  };

  const publishFolder = async (folderId: number) => {
    setPublishingFolderId(folderId);
    try {
      await api.patch(`/video/admin/folders/${folderId}`, { is_active: true });
      message.success('Папка опубликована. Доступные видео появились у сотрудников');
      setFolderFilter('all');
      setFolderExpanded(folderId, true);
      await loadData();
    } catch (err: any) {
      message.error(getApiErrorMessage(err, 'Не удалось опубликовать папку'));
    } finally {
      setPublishingFolderId(null);
    }
  };

  const deleteFolder = async (folderId: number) => {
    setDeletingFolderId(folderId);
    try {
      await api.delete(`/video/admin/folders/${folderId}/permanent`);
      message.success('Папка, её видео, файлы и прогресс просмотров удалены');
      await loadData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Не удалось удалить папку');
    } finally {
      setDeletingFolderId(null);
    }
  };

  const openUploadModal = (folderId?: number) => {
    uploadForm.resetFields();
    const targetFolderId = folderId ?? selectedFolder?.id;
    uploadForm.setFieldsValue({
      folder_id: folders.find(folder => folder.id === targetFolderId && folder.is_active)?.id,
      sort_order: 0,
    });
    setUploadFile(null);
    setUploadPercent(0);
    setUploadModalOpen(true);
  };

  const uploadVideo = async () => {
    if (uploading) return;
    const values = await uploadForm.validateFields();
    if (!uploadFile) {
      message.error('Выберите MP4 или WebM файл');
      return;
    }
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, key === 'training_date' ? (value as Dayjs).format('YYYY-MM-DD') : String(value));
      }
    });
    formData.append('file', uploadFile);
    setUploadPercent(0);
    setUploading(true);
    try {
      const { data: uploadedVideo } = await api.post<TrainingVideo>('/video/admin/videos', formData, {
        // Large training recordings can take longer than the shared five-minute limit.
        timeout: 0,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (event.total) setUploadPercent(Math.round(event.loaded / event.total * 100));
        },
      });
      message.success('Видео загружено');
      setUploadModalOpen(false);
      setFolderExpanded(uploadedVideo.folder_id, true);
      setFolderFilter('all');
      setFolderSearch('');
      setViewMode('content');
      loadData();
    } catch (err: any) {
      message.error(trainingUploadError(err, 'Ошибка загрузки видео'));
    } finally {
      setUploading(false);
    }
  };

  const openVideoModal = (video: TrainingVideo) => {
    setEditingVideo(video);
    videoForm.setFieldsValue({
      ...video,
      training_date: video.training_date ? dayjs(video.training_date) : null,
    });
  };

  const saveVideo = async () => {
    if (!editingVideo || savingVideo) return;
    setSavingVideo(true);
    try {
      const values = await videoForm.validateFields();
      await api.patch(`/video/admin/videos/${editingVideo.id}`, {
        ...values,
        training_date: values.training_date.format('YYYY-MM-DD'),
      });
      message.success('Видео обновлено');
      setEditingVideo(null);
      loadData();
    } catch (err: any) {
      if (!err?.errorFields) message.error(getApiErrorMessage(err, 'Не удалось сохранить видео'));
    } finally {
      setSavingVideo(false);
    }
  };

  const setVideoPublished = async (video: TrainingVideo, isActive: boolean) => {
    setUpdatingVideoId(video.id);
    try {
      await api.patch(`/video/admin/videos/${video.id}`, { is_active: isActive });
      message.success(isActive ? 'Видео опубликовано' : 'Видео скрыто от сотрудников');
      await loadData();
    } catch (err: any) {
      message.error(getApiErrorMessage(err, isActive ? 'Не удалось опубликовать видео' : 'Не удалось скрыть видео'));
    } finally {
      setUpdatingVideoId(null);
    }
  };

  const deleteVideo = async (videoId: number) => {
    setDeletingVideoId(videoId);
    try {
      await api.delete(`/video/admin/videos/${videoId}`);
      message.success('Видео, файл и прогресс просмотров удалены');
      await loadData();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Не удалось удалить видео');
    } finally {
      setDeletingVideoId(null);
    }
  };

  const allVideos = useMemo(() => folders.flatMap(folder => folder.videos || []), [folders]);
  const activeFoldersCount = useMemo(() => folders.filter(folder => folder.is_active).length, [folders]);
  const completedViewsCount = useMemo(() => statistics.filter(row => row.is_completed).length, [statistics]);
  const uniqueViewersCount = useMemo(() => new Set(statistics.map(row => row.user_id)).size, [statistics]);

  const filteredFolders = useMemo(() => {
    const query = folderSearch.trim().toLocaleLowerCase('ru');
    return folders.filter((folder) => {
      if (folderFilter === 'active' && !folder.is_active) return false;
      if (folderFilter === 'archived' && folder.is_active) return false;
      if (!query) return true;
      return `${folder.title} ${folder.description || ''}`.toLocaleLowerCase('ru').includes(query);
    });
  }, [folderFilter, folderSearch, folders]);

  const selectedFolder = useMemo(() => (
    filteredFolders.find(folder => folder.id === selectedFolderId) || filteredFolders[0] || null
  ), [filteredFolders, selectedFolderId]);

  const filteredStatistics = useMemo(() => {
    const query = statisticsSearch.trim().toLocaleLowerCase('ru');
    return statistics.filter((row) => {
      if (statisticsFilter === 'completed' && !row.is_completed) return false;
      if (statisticsFilter === 'progress' && row.is_completed) return false;
      if (!query) return true;
      return `${row.user_name} ${row.user_email} ${row.folder_title} ${row.video_title}`
        .toLocaleLowerCase('ru')
        .includes(query);
    });
  }, [statistics, statisticsFilter, statisticsSearch]);

  return (
    <Layout wide>
      <div data-guide="admin-materials-home" className="video-admin-page">
        <header className="video-admin-page-header">
          <div>
            <Title level={1}>Управление видеопорталом</Title>
            <Paragraph>Папки, видео, презентации и транскрибации обучений.</Paragraph>
          </div>
          <div className="video-admin-header-actions">
            <Button data-guide="upload-video" icon={<UploadOutlined />} onClick={() => openUploadModal()}>Загрузить видео</Button>
            <Button type="primary" icon={<FolderAddOutlined />} onClick={() => openFolderModal()}>Создать папку</Button>
          </div>
        </header>

        {error && <Alert type="error" showIcon message={error} className="video-admin-alert" />}
        {statisticsError && <Alert type="warning" showIcon message={statisticsError} className="video-admin-alert" />}

        <section className="video-admin-summary" aria-label="Сводка видеопортала">
          <article className="video-admin-summary-item primary">
            <span><FolderOpenOutlined /></span><div><strong>{folders.length}</strong><small>Всего папок</small></div>
          </article>
          <article className="video-admin-summary-item success">
            <span><CheckCircleOutlined /></span><div><strong>{activeFoldersCount}</strong><small>Опубликовано папок</small></div>
          </article>
          <article className="video-admin-summary-item primary">
            <span><VideoCameraOutlined /></span><div><strong>{allVideos.length}</strong><small>Видеофайлов</small></div>
          </article>
          <article className="video-admin-summary-item neutral">
            <span><TeamOutlined /></span><div><strong>{uniqueViewersCount}</strong><small>Участников просмотра</small></div>
          </article>
          <article className="video-admin-summary-item success">
            <span><EyeOutlined /></span><div><strong>{completedViewsCount}</strong><small>Завершено просмотров</small></div>
          </article>
        </section>

        <div className="video-admin-view-tabs" role="group" aria-label="Раздел видеопортала">
          <button type="button" aria-pressed={viewMode === 'content'} className={viewMode === 'content' ? 'active' : ''} onClick={() => setViewMode('content')}>
            <FolderOpenOutlined /><span>Материалы</span><b>{folders.length}</b>
          </button>
          <button data-guide="admin-statistics-tab" type="button" aria-pressed={viewMode === 'statistics'} className={viewMode === 'statistics' ? 'active' : ''} onClick={() => setViewMode('statistics')}>
            <EyeOutlined /><span>Статистика</span><b>{statistics.length}</b>
          </button>
        </div>

        {viewMode === 'content' ? (
          <>
            <section className="video-admin-section">
              <div className="video-admin-section-header">
                <div>
                  <Title level={2}>Папки обучений</Title>
                  <Text type="secondary">Раскройте папку, чтобы увидеть её видео и документы</Text>
                </div>
                <Space wrap className="video-admin-section-toolbar">
                  <Input
                    className="video-admin-search"
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder="Найти папку"
                    aria-label="Поиск папки обучения"
                    value={folderSearch}
                    onChange={event => setFolderSearch(event.target.value)}
                  />
                  <Button icon={<ReloadOutlined />} loading={loading} onClick={loadData}>Обновить</Button>
                </Space>
              </div>

              <div className="video-admin-filter-row">
                <Button type={folderFilter === 'all' ? 'primary' : 'default'} onClick={() => setFolderFilter('all')}>
                  Все папки <span className="video-admin-filter-count">{folders.length}</span>
                </Button>
                <Button type={folderFilter === 'active' ? 'primary' : 'default'} onClick={() => setFolderFilter('active')}>
                  Опубликованные <span className="video-admin-filter-count">{activeFoldersCount}</span>
                </Button>
                <Button type={folderFilter === 'archived' ? 'primary' : 'default'} onClick={() => setFolderFilter('archived')}>
                  Архив <span className="video-admin-filter-count">{folders.length - activeFoldersCount}</span>
                </Button>
              </div>

              <div hidden={filteredFolders.length === 0}>
                <ul className="video-admin-content-list" aria-label="Папки обучений">
                  {/* Keep folder contents mounted so collapsing or filtering retains drafts and pending uploads. */}
                  {folders.map((folder) => {
                    const isExpanded = expandedFolderIds.has(folder.id);
                    const isVisible = filteredFolders.some(item => item.id === folder.id);
                    return (
                      <li
                        key={folder.id}
                        data-folder-id={folder.id}
                        className="video-admin-folder-branch"
                        hidden={!isVisible}
                      >
                        <div className={`video-admin-content-row video-admin-folder-row ${isExpanded ? 'selected' : ''} ${folder.is_active ? '' : 'archived'}`}>
                          <div className="video-admin-row-main">
                            <div className="video-admin-row-heading">
                              <button
                                type="button"
                                data-guide={folder.is_active ? "admin-folder-toggle" : undefined}
                                id={`admin-folder-toggle-${folder.id}`}
                                className="video-admin-folder-select"
                                aria-expanded={isExpanded}
                                aria-controls={`admin-folder-contents-${folder.id}`}
                                onClick={() => setFolderExpanded(folder.id, !isExpanded)}
                                onKeyDown={event => {
                                  if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                                    event.preventDefault();
                                    setFolderExpanded(folder.id, event.key === 'ArrowRight');
                                  }
                                }}
                              >
                                <RightOutlined className="video-admin-folder-chevron" aria-hidden="true" />
                                {isExpanded ? <FolderOpenOutlined aria-hidden="true" /> : <FolderOutlined aria-hidden="true" />}
                                <span>{folder.title}</span>
                              </button>
                              <Tag color={folder.is_active ? 'success' : 'default'}>{folder.is_active ? 'Опубликована' : 'Архив'}</Tag>
                            </div>
                            {folder.description && <Paragraph className="video-admin-row-description">{folder.description}</Paragraph>}
                            <div className="video-admin-row-meta">
                              <span><VideoCameraOutlined /> {folder.video_count} видео</span>
                              <span><FileOutlined /> {folder.document_count || 0} документов</span>
                            </div>
                          </div>
                          <div className="video-admin-row-actions">
                            <Button icon={<UploadOutlined />} disabled={!folder.is_active} onClick={() => openUploadModal(folder.id)}>Загрузить видео</Button>
                            <Button icon={<EditOutlined />} onClick={() => openFolderModal(folder)}>Изменить</Button>
                            {folder.is_active && (
                              <Popconfirm title="Архивировать папку и скрыть её материалы?" onConfirm={() => archiveFolder(folder.id)}>
                                <Button icon={<InboxOutlined />} title="Архивировать" aria-label={`Архивировать папку «${folder.title}»`}>В архив</Button>
                              </Popconfirm>
                            )}
                            {!folder.is_active && (
                              <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                loading={publishingFolderId === folder.id}
                                onClick={() => publishFolder(folder.id)}
                              >
                                Опубликовать
                              </Button>
                            )}
                            <Popconfirm
                              title={`Удалить папку «${folder.title}»?`}
                              description={folder.video_count + (folder.document_count || 0) > 0
                                ? `Безвозвратно удалятся ${folder.video_count} видео и ${folder.document_count || 0} документов, файлы и прогресс сотрудников.`
                                : 'Папка будет удалена без возможности восстановления.'}
                              okText="Удалить"
                              cancelText="Отмена"
                              okButtonProps={{ danger: true, loading: deletingFolderId === folder.id }}
                              onConfirm={() => deleteFolder(folder.id)}
                            >
                              <Button danger icon={<DeleteOutlined />} loading={deletingFolderId === folder.id} title="Удалить безвозвратно" aria-label={`Удалить папку «${folder.title}»`}>Удалить</Button>
                            </Popconfirm>
                          </div>
                        </div>
                        <div
                          id={`admin-folder-contents-${folder.id}`}
                          className="video-admin-folder-contents"
                          role="region"
                          data-guide={folder.is_active ? "admin-folder-contents" : undefined}
                          aria-labelledby={`admin-folder-toggle-${folder.id}`}
                          hidden={!isExpanded}
                        >
                          <AdminTrainingDocuments folders={folders} folder={folder} onRefresh={loadData} embedded onOpen={document => setPreview({ folderId: folder.id, kind: 'document', id: document.id })} />

                          <section className="video-admin-section video-admin-videos-section">
                            <div className="video-admin-section-header">
                              <div>
                                <Title level={3}>Видео</Title>
                              </div>
                              {folder.is_active && (
                                <Button type="primary" icon={<UploadOutlined />} onClick={() => openUploadModal(folder.id)}>
                                  Загрузить видео
                                </Button>
                              )}
                            </div>

                            {folder.videos.length ? (
                              <ul className="video-admin-content-list" aria-label="Видео в папке">
                                {folder.videos.map((video, index) => (
                                  <li key={video.id} className={`video-admin-content-row ${video.is_active && folder.is_active ? '' : 'hidden'}`}>
                                    <span className="video-admin-row-icon" aria-hidden="true"><PlayCircleOutlined /></span>
                                    <div className="video-admin-row-main">
                                      <div className="video-admin-row-heading">
                                        <Title level={3}><button type="button" className="video-admin-material-open" aria-label={`Открыть видео «${video.title}»`} onClick={() => setPreview({ folderId: folder.id, kind: 'video', id: video.id })}>{video.title}</button></Title>
                                        <Tag color={video.is_active && folder.is_active ? 'success' : 'default'}>
                                          {!folder.is_active ? 'Папка в архиве' : video.is_active ? 'Доступно' : 'Скрыто'}
                                        </Tag>
                                      </div>
                                      <Paragraph className="video-admin-row-description">{video.description || video.original_filename}</Paragraph>
                                      <div className="video-admin-row-meta">
                                        <span>Видео {index + 1}</span>
                                        <span><CalendarOutlined /> Дата обучения: {formatTrainingDate(video.training_date)}</span>
                                        {inferTrainingDate(video.original_filename) && <span>Дата записи из имени файла: {formatTrainingDate(inferTrainingDate(video.original_filename))}</span>}
                                        <span>Загружено: {formatUploadDate(video.uploaded_at)} МСК</span>
                                        <span><FileOutlined /> {formatBytes(video.size_bytes)}</span>
                                        <span><EyeOutlined /> {formatDuration(video.duration_seconds)}</span>
                                      </div>
                                      {dateMismatchMessage(video.training_date, video.original_filename) && <Alert className="video-admin-row-warning" type="warning" showIcon message={dateMismatchMessage(video.training_date, video.original_filename)} />}
                                    </div>
                                    <div className="video-admin-row-actions">
                                      <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => setPreview({ folderId: folder.id, kind: 'video', id: video.id })}>Смотреть</Button>
                                      <Button
                                        type={video.is_active ? 'default' : 'primary'}
                                        icon={video.is_active ? <InboxOutlined /> : <CheckCircleOutlined />}
                                        loading={updatingVideoId === video.id}
                                        disabled={!folder.is_active}
                                        onClick={() => setVideoPublished(video, !video.is_active)}
                                      >
                                        {video.is_active ? 'Скрыть' : 'Опубликовать'}
                                      </Button>
                                      <Button icon={<EditOutlined />} onClick={() => openVideoModal(video)}>Изменить</Button>
                                      <Button data-guide="replace-file" icon={<SwapOutlined />} onClick={() => setReplacingVideo(video)}>Заменить файл</Button>
                                      <Button onClick={() => setSubtitleVideo(video)}>Субтитры</Button>
                                      <Popconfirm
                                        title={`Удалить видео «${video.title}»?`}
                                        description="Материал и прогресс сотрудников будут удалены. Для обновления файла с сохранением статистики используйте «Заменить файл»."
                                        okText="Удалить"
                                        cancelText="Отмена"
                                        okButtonProps={{ danger: true, loading: deletingVideoId === video.id }}
                                        onConfirm={() => deleteVideo(video.id)}
                                      >
                                        <Button danger icon={<DeleteOutlined />} loading={deletingVideoId === video.id}>Удалить</Button>
                                      </Popconfirm>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <Empty
                                className="video-admin-empty"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="В этой папке пока нет видео"
                              >
                                {folder.is_active && <Button type="primary" icon={<UploadOutlined />} onClick={() => openUploadModal(folder.id)}>Загрузить первое видео</Button>}
                              </Empty>
                            )}
                          </section>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {!filteredFolders.length && <Empty className="video-admin-empty" description={loading ? 'Загрузка папок...' : 'Папки не найдены'} />}
            </section>
          </>
        ) : (
          <section className="video-admin-section video-admin-statistics-section">
            <div className="video-admin-section-header">
              <div>
                <Title level={2}>Статистика просмотров</Title>
                <Text type="secondary">Прогресс сотрудников по каждому видео</Text>
              </div>
              <Input
                className="video-admin-search"
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Сотрудник, папка или видео"
                aria-label="Поиск сотрудника, папки или видео"
                value={statisticsSearch}
                onChange={event => setStatisticsSearch(event.target.value)}
              />
            </div>

            <div className="video-admin-filter-row">
              <Button type={statisticsFilter === 'all' ? 'primary' : 'default'} onClick={() => setStatisticsFilter('all')}>
                Все просмотры <span className="video-admin-filter-count">{statistics.length}</span>
              </Button>
              <Button type={statisticsFilter === 'completed' ? 'primary' : 'default'} onClick={() => setStatisticsFilter('completed')}>
                Завершено <span className="video-admin-filter-count">{completedViewsCount}</span>
              </Button>
              <Button type={statisticsFilter === 'progress' ? 'primary' : 'default'} onClick={() => setStatisticsFilter('progress')}>
                В процессе <span className="video-admin-filter-count">{statistics.length - completedViewsCount}</span>
              </Button>
            </div>

            {filteredStatistics.length ? (
              <ul className="video-admin-content-list" aria-label="Статистика просмотров">
                {filteredStatistics.map((row) => (
                  <li key={`${row.user_id}-${row.video_id}`} className={`video-admin-content-row ${row.is_completed ? 'completed' : ''}`}>
                    <span className="video-admin-row-icon" aria-hidden="true"><TeamOutlined /></span>
                    <div className="video-admin-row-main">
                      <div className="video-admin-row-heading">
                        <strong>{row.user_name}</strong>
                        <Tag color={row.is_completed ? 'success' : 'processing'}>{row.is_completed ? 'Завершено' : 'В процессе'}</Tag>
                      </div>
                      <Paragraph className="video-admin-row-description">{row.video_title}</Paragraph>
                      <div className="video-admin-row-meta">
                        <span>{row.user_email}</span>
                        <span><FolderOpenOutlined /> {row.folder_title}</span>
                        <span><CalendarOutlined /> {dayjs(row.last_viewed_at).format('DD.MM.YYYY HH:mm')}</span>
                      </div>
                    </div>
                    <div className="video-admin-row-progress">
                      <Progress
                        percent={Math.round(row.percent_watched)}
                        size="small"
                        strokeColor={row.is_completed ? '#12b76a' : '#2563eb'}
                        trailColor="#eaecf0"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty className="video-admin-empty" description={loading ? 'Загрузка статистики...' : 'Данные по просмотрам не найдены'} />
            )}
          </section>
        )}
      </div>

      <Modal className="video-admin-modal" title={editingFolder ? 'Редактировать папку' : 'Новая папка обучения'} open={folderModalOpen} onCancel={() => !savingFolder && setFolderModalOpen(false)} onOk={saveFolder} confirmLoading={savingFolder} okText="Сохранить">
        <Form form={folderForm} layout="vertical" disabled={savingFolder}>
          <Form.Item name="title" label="Название" rules={[{ required: true }]}><Input maxLength={255} /></Form.Item>
          <Form.Item name="description" label="Описание"><TextArea rows={4} maxLength={5000} /></Form.Item>
          <Form.Item name="sort_order" label="Порядок"><InputNumber min={-100000} max={100000} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="is_active" label="Доступна сотрудникам" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal className="video-admin-modal" title="Загрузка видео" open={uploadModalOpen} onCancel={() => !uploading && setUploadModalOpen(false)} onOk={uploadVideo} okButtonProps={{ loading: uploading }} okText="Загрузить" closable={!uploading} maskClosable={!uploading}>
        <Form data-guide="video-upload-form" form={uploadForm} layout="vertical" disabled={uploading}>
          <Form.Item data-guide="video-upload-folder" name="folder_id" label="Папка" rules={[{ required: true }]}><Select options={folders.filter((folder) => folder.is_active).map((folder) => ({ value: folder.id, label: folder.title }))} /></Form.Item>
          <Form.Item name="title" label="Название видео" rules={[{ required: true }]}><Input maxLength={255} /></Form.Item>
          <Form.Item data-guide="video-upload-date" name="training_date" label="Дата обучения" extra="При выборе файла дата берётся из его имени. Дата загрузки сохраняется отдельно." rules={[{ required: true, message: 'Укажите дату обучения' }]}><DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="description" label="Описание"><TextArea rows={3} maxLength={5000} /></Form.Item>
          <Form.Item name="sort_order" label="Порядок"><InputNumber style={{ width: '100%' }} /></Form.Item>
          <Form.Item label="Файл MP4 или WebM" required>
            <Upload beforeUpload={(file) => {
              const validation = validateTrainingFile(file, 'video');
              if (validation) { message.error(validation); return Upload.LIST_IGNORE; }
              setUploadFile(file);
              const inferred = inferTrainingDate(file.name);
              uploadForm.setFieldValue('training_date', inferred ? dayjs(inferred) : null);
              return false;
            }} fileList={uploadFile ? [{ uid: '-1', name: uploadFile.name, status: 'done' }] : []} onRemove={() => { setUploadFile(null); return true; }} accept="video/mp4,video/webm" maxCount={1}>
              <Button data-guide="video-upload-file" icon={<UploadOutlined />}>Выбрать файл</Button>
            </Upload>
          </Form.Item>
          {uploadFile && inferTrainingDate(uploadFile.name) && <Paragraph type="secondary">Дата записи из имени файла: {formatTrainingDate(inferTrainingDate(uploadFile.name))}</Paragraph>}
          {uploadDateWarning && <Alert type="warning" showIcon message={uploadDateWarning} action={<Button onClick={() => uploadForm.setFieldValue('training_date', dayjs(inferTrainingDate(uploadFile!.name)))}>Использовать дату записи</Button>} />}
          {uploading && <>
            <Progress percent={uploadPercent} status="active" />
            <Text type="secondary">{uploadPercent >= 100 ? 'Файл передан. Ожидаем подтверждение сохранения на сервере…' : 'Передаём видео. Не закрывайте страницу до завершения загрузки.'}</Text>
          </>}
        </Form>
      </Modal>

      <Modal className="video-admin-modal" title="Редактировать видео" open={Boolean(editingVideo)} onCancel={() => !savingVideo && setEditingVideo(null)} onOk={saveVideo} confirmLoading={savingVideo} okText="Сохранить">
        <Form form={videoForm} layout="vertical" disabled={savingVideo}>
          <Form.Item name="folder_id" label="Папка" rules={[{ required: true }]}><Select options={folders.map((folder) => ({ value: folder.id, label: folder.title }))} /></Form.Item>
          <Form.Item name="title" label="Название" rules={[{ required: true }]}><Input maxLength={255} /></Form.Item>
          <Form.Item name="training_date" label="Дата обучения" rules={[{ required: true, message: 'Укажите дату обучения' }]}><DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} /></Form.Item>
          {editingVideo && inferTrainingDate(editingVideo.original_filename) && <Paragraph type="secondary">Дата записи из имени файла: {formatTrainingDate(inferTrainingDate(editingVideo.original_filename))}</Paragraph>}
          {editedDateWarning && <Alert type="warning" showIcon message={editedDateWarning} action={<Button onClick={() => videoForm.setFieldValue('training_date', dayjs(inferTrainingDate(editingVideo!.original_filename)))}>Использовать дату записи</Button>} />}
          <Form.Item name="description" label="Описание"><TextArea rows={3} maxLength={5000} /></Form.Item>
          <Form.Item name="sort_order" label="Порядок"><InputNumber style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="is_active" label="Доступно сотрудникам" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
      {preview && <AdminTrainingPreview key={`${preview.kind}:${preview.id}`} folder={folders.find(folder => folder.id === preview.folderId)} selection={preview} onClose={() => setPreview(null)} onRefresh={loadData} />}
      {subtitleVideo && <AdminVideoSubtitlesModal key={`${subtitleVideo.id}:${subtitleVideo.file_revision}`} video={subtitleVideo} onClose={() => setSubtitleVideo(null)} />}
      {replacingVideo && <ReplaceTrainingFile key={replacingVideo.id} material={replacingVideo} kind="video" onClose={() => setReplacingVideo(null)} onReplaced={loadData} />}
    </Layout>
  );
};

export default AdminVideoPortal;
